// src/components/quality/CustomerAssessmentForm.js
import { useState, useEffect } from 'react';
import api from '../../services/api';
import useAuth from '../../hooks/useAuth';

const CustomerAssessmentForm = ({ projectId, onClose, assessment: initialAssessment, onAssessmentUpdated }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    call_datetime: '',
    phone_number: '',
    operator_id: '',
    operator_name: '',
    agent_name: '',
    customer_question: '',
    operator_response: '',
    internal_comment: '',
    needs_os: false,
    comment_for_send: '',
    os_given: false,
    os_datetime: '',
    theme: '',
    subtheme: ''
  });
  
  // Состояние для операторов
  const [fieldOptions, setFieldOptions] = useState({
    operators: []
  });

  // Состояние для тем и подтем
  const [themes, setThemes] = useState([]);
  const [subthemes, setSubthemes] = useState([]);
  const [loadingThemes, setLoadingThemes] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  // Загрузка данных редактируемой оценки
  const [hasLoaded, setHasLoaded] = useState(false);
  useEffect(() => {
    if (initialAssessment && !hasLoaded) {
      setIsEditing(true);
      
      // Находим оператора по ID для получения ФИО
      const operator = fieldOptions.operators.find(op => op.id === initialAssessment.operator_id);
      
      setFormData(prev => ({
        ...prev,
        call_datetime: initialAssessment.call_datetime ? initialAssessment.call_datetime.slice(0, 16) : '',
        phone_number: initialAssessment.phone_number || '',
        operator_id: initialAssessment.operator_id || '',
        operator_name: operator ? `${operator.surname} ${operator.name}` : initialAssessment.operator_name || '',
        agent_name: operator ? `${operator.surname} ${operator.name}` : initialAssessment.operator_name || '',
        customer_question: initialAssessment.customer_question || '',
        operator_response: initialAssessment.operator_response || '',
        internal_comment: initialAssessment.internal_comment || '',
        needs_os: initialAssessment.needs_os || false,
        comment_for_send: initialAssessment.comment_for_send || '',
        os_given: initialAssessment.os_given || false,
        os_datetime: initialAssessment.os_datetime ? initialAssessment.os_datetime.slice(0, 16) : '',
        theme: initialAssessment.theme || '',
        subtheme: initialAssessment.subtheme || ''
      }));
      
      setHasLoaded(true);
    }
  }, [initialAssessment, fieldOptions.operators, hasLoaded]);

  const [projectName, setProjectName] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Загрузка данных проекта по API
  useEffect(() => {
    if (!projectId) return;

    const fetchData = async () => {
      try {
        // Получение операторов
        const operatorsResponse = await api.get(`/operators?project_id=${projectId}`);
        setFieldOptions(prev => ({
          ...prev,
          operators: operatorsResponse.data.operators || []
        }));
        

        
        setProjectName('Проект'); // Имя проекта не возвращается, оставляем заглушку
      } catch (err) {
        console.error('Ошибка загрузки данных:', err);
        setError('Не удалось загрузить данные. Проверьте подключение или обратитесь к администратору.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId]);

  // Загрузка тем для проекта
  useEffect(() => {
    if (!projectId) return;

    const fetchThemes = async () => {
      setLoadingThemes(true);
      try {
        const response = await api.get(`/assessments-customer-themes/${projectId}`);
        const themesData = response.data.themes || [];
        setThemes(themesData);

        // Если оценка уже имеет тему, устанавливаем соответствующие подтемы
        if (initialAssessment && initialAssessment.theme) {
          const selectedTheme = themesData.find(t => t.name === initialAssessment.theme);
          if (selectedTheme) {
            setSubthemes(selectedTheme.subthemes || []);
          }
        }
        
        // Если есть только одна тема, подставляем её
        else if (themesData.length === 1) {
          setFormData(prev => ({
            ...prev,
            theme: themesData[0].name
          }));
          // Загружаем подтемы для этой темы
          setSubthemes(themesData[0].subthemes || []);
        }
      } catch (err) {
        console.error('Ошибка загрузки тем:', err);
        setError('Не удалось загрузить темы для выбора');
      } finally {
        setLoadingThemes(false);
      }
    };

    fetchThemes();
  }, [projectId, initialAssessment]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'os_datetime') {
      // Если меняем дату ОС, сохраняем только дату, время будет подставлено при отправке
      setFormData(prev => ({ 
        ...prev, 
        os_datetime: value
      }));
    } else {
      setFormData(prev => ({ 
        ...prev, 
        [name]: type === 'checkbox' ? checked : value 
      }));
    }
  };
  
  // Обработчик выбора темы
  const handleThemeChange = (e) => {
    const themeName = e.target.value;
    const selectedTheme = themes.find(t => t.name === themeName);
    
    setFormData(prev => ({
      ...prev,
      theme: themeName,
      subtheme: '' // Сбрасываем подтему при смене темы
    }));
    
    // Устанавливаем подтемы для выбранной темы
    if (selectedTheme) {
      setSubthemes(selectedTheme.subthemes || []);
    } else {
      setSubthemes([]);
    }
  };

  const handleOperatorChange = (e) => {
    const selectedId = e.target.value;
    const selectedOperator = fieldOptions.operators.find(op => op.id.toString() === selectedId);
    if (selectedOperator) {
      setFormData(prev => ({
        ...prev,
        operator_id: selectedOperator.id,
        operator_name: `${selectedOperator.surname} ${selectedOperator.name}`,
        agent_name: `${selectedOperator.surname} ${selectedOperator.name}`
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      // Проверка обязательных полей
      if (!formData.call_datetime) {
        setError('Пожалуйста, заполните обязательное поле: Дата и время звонка');
        setSubmitting(false);
        return;
      }
      
      if (!formData.phone_number.trim()) {
        setError('Пожалуйста, заполните обязательное поле: Номер телефона');
        setSubmitting(false);
        return;
      }
      
      if (!formData.operator_id) {
        setError('Пожалуйста, заполните обязательное поле: Оператор');
        setSubmitting(false);
        return;
      }
      
      if (!formData.customer_question.trim()) {
        setError('Пожалуйста, заполните обязательное поле: Вопрос клиента');
        setSubmitting(false);
        return;
      }
      
      if (!formData.operator_response.trim()) {
        setError('Пожалуйста, заполните обязательное поле: Ответ оператора');
        setSubmitting(false);
        return;
      }

      // Подготовка данных для отправки
      const payload = {
        project_id: projectId,
        call_datetime: formData.call_datetime,
        phone_number: formData.phone_number,
        operator_id: formData.operator_id,
        customer_question: formData.customer_question,
        operator_response: formData.operator_response,
        internal_comment: formData.internal_comment,
        needs_os: formData.needs_os,
        comment_for_send: formData.comment_for_send,
        os_given: formData.os_given,
        os_datetime: formData.os_given && formData.os_datetime ? `${formData.os_datetime}T12:00:00` : null,
        theme: formData.theme,
        subtheme: formData.subtheme
      };

      await api.put(`/quality/assessment-customer/${initialAssessment.id}`, payload);
      
      // Успешное обновление
      if (onClose) onClose();
      if (onAssessmentUpdated) onAssessmentUpdated();
    } catch (err) {
      setError('Не удалось сохранить оценку от клиента. Попробуйте ещё раз.');
      console.error('Ошибка сохранения оценки от клиента:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="modal-content">
        <h3>Загрузка данных...</h3>
        <div>Пожалуйста, подождите</div>
      </div>
    );
  }

  return (
    <div className="modal-content">
      <div className="modal-header">
        <h3>Оценка качества от клиента</h3>
        <button type="button" className="text-modal-close" onClick={onClose} title="Закрыть">
          ×
        </button>
      </div>

      {error && <div className="form-error">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="call_datetime">Дата и время звонка *</label>
          <input
            type="datetime-local"
            id="call_datetime"
            name="call_datetime"
            value={formData.call_datetime}
            onChange={handleChange}
            step="1"
            required
          />
          <small>Поле обязательно для заполнения</small>
        </div>

        <div className="form-group">
          <label htmlFor="phone_number">Номер телефона *</label>
          <input
            type="text"
            id="phone_number"
            name="phone_number"
            value={formData.phone_number}
            onChange={handleChange}
            placeholder="+79991234567"
            required
          />
          <small>Поле обязательно для заполнения</small>
        </div>

        <div className="form-group">
          <label htmlFor="operator_id">Оператор *</label>
          {fieldOptions.operators.length > 0 ? (
            <select
              id="operator_id"
              name="operator_id"
              value={formData.operator_id}
              onChange={handleOperatorChange}
              required
            >
              <option value="">-- Выберите оператора --</option>
              {fieldOptions.operators.map(operator => (
                <option key={operator.id} value={operator.id}>
                  {`${operator.surname} ${operator.name}`}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              id="operator_id"
              name="operator_name"
              value={formData.operator_name}
              onChange={handleChange}
              placeholder="Введите ФИО оператора"
              required
            />
          )}
          <small>Поле обязательно для заполнения</small>
        </div>

        <div className="form-group">
          <label htmlFor="customer_question">Вопрос клиента *</label>
          <textarea
            id="customer_question"
            name="customer_question"
            rows="3"
            value={formData.customer_question}
            onChange={handleChange}
            placeholder="Введите вопрос клиента"
            required
            style={{width: '100%'}}
          ></textarea>
          <small>Поле обязательно для заполнения</small>
        </div>

        <div className="form-group">
          <label htmlFor="operator_response">Ответ оператора *</label>
          <textarea
            id="operator_response"
            name="operator_response"
            rows="3"
            value={formData.operator_response}
            onChange={handleChange}
            placeholder="Введите ответ оператора"
            required
            style={{width: '100%'}}
          ></textarea>
          <small>Поле обязательно для заполнения</small>
        </div>

        <div className="form-group">
          <label htmlFor="internal_comment">Внутренний комментарий</label>
          <textarea
            id="internal_comment"
            name="internal_comment"
            rows="3"
            value={formData.internal_comment}
            onChange={handleChange}
            placeholder="Введите внутренний комментарий"
            style={{width: '100%'}}
          ></textarea>
        </div>

        <div className="form-group">
          <label htmlFor="needs_os">Требуется ОС</label>
          <input
            type="checkbox"
            id="needs_os"
            name="needs_os"
            checked={formData.needs_os}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="comment_for_send">Комментарий для отправки оператору</label>
          <textarea
            id="comment_for_send"
            name="comment_for_send"
            rows="3"
            value={formData.comment_for_send}
            onChange={handleChange}
            placeholder="Введите комментарий для отправки оператору"
            style={{width: '100%'}}
          ></textarea>
        </div>



        <div className="form-group">
          <label htmlFor="os_given">Дана ОС</label>
          <input
            type="checkbox"
            id="os_given"
            name="os_given"
            checked={formData.os_given}
            onChange={handleChange}
          />
        </div>
        
        {formData.os_given && (
          <div className="form-group">
            <label htmlFor="os_datetime">Дата ОС</label>
            <input
              type="date"
              id="os_datetime"
              name="os_datetime"
              value={formData.os_datetime?.split('T')[0] || ''}
              onChange={handleChange}
            />
          </div>
        )}

        <div className="form-group">
            <label htmlFor="theme">Тема</label>
            {loadingThemes ? (
              <span>Загрузка тем...</span>
            ) : (
              <select
                id="theme"
                name="theme"
                value={formData.theme}
                onChange={handleThemeChange}
                style={{width: '100%'}}
              >
                <option value="">-- Выберите тему --</option>
                {themes.map(theme => (
                  <option key={theme.id} value={theme.name}>
                    {theme.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="subtheme">Подтема</label>
            {subthemes.length === 0 ? (
              <span>{formData.theme ? 'Нет доступных подтем' : 'Выберите тему для отображения подтем'}</span>
            ) : (
              <select
                id="subtheme"
                name="subtheme"
                value={formData.subtheme}
                onChange={handleChange}
                style={{width: '100%'}}
              >
                <option value="">-- Выберите подтему --</option>
                {subthemes.map(subtheme => (
                  <option key={subtheme.id} value={subtheme.name}>
                    {subtheme.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="modal-actions">
          {onClose && (
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={submitting}
            >
              Отмена
            </button>
          )}
          <button
            type="submit"
            className="btn-submit"
            disabled={submitting}
          >
            {submitting ? 'Сохранение...' : 'Сохранить оценку'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CustomerAssessmentForm;